import type { ReactNode } from 'react'

/**
 * Briques d'interface communes.
 *
 * Deux contraintes iOS sont appliquées ici une fois pour toutes plutôt que
 * répétées dans chaque écran :
 *
 * - toute cible tactile fait au moins 44 px de haut (recommandation Apple) ;
 * - tout champ de saisie fait au moins 16 px de police, sinon Safari zoome
 *   automatiquement au premier appui et l'utilisateur se retrouve à faire
 *   défiler la page pendant la saisie d'un ordre.
 */

export function Card({
  title,
  action,
  children,
}: {
  title?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    // `border-border/70` et un fond légèrement plus clair sur l'en-tête : les
    // cartes se détachaient mal du fond quand plusieurs s'empilent, et on ne
    // savait plus où l'une finissait.
    <section className="overflow-hidden rounded-lg border border-border/70 bg-panel">
      {title && (
        <div className="flex items-center justify-between gap-2 border-b border-border/70 bg-elevated/30 px-3 py-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
            {title}
          </h2>
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

/** Champ numérique : clavier décimal, 16 px, hauteur tactile. */
export function NumberField({
  label,
  value,
  onChange,
  suffix,
  placeholder,
  disabled,
  tone = 'normal',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  suffix?: string
  placeholder?: string
  disabled?: boolean
  tone?: 'normal' | 'accent'
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <div
        className={`flex min-h-touch items-center gap-2 rounded-md border bg-elevated px-3 ${
          tone === 'accent' ? 'border-accent/60' : 'border-border'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <input
          // `inputMode="decimal"` ouvre le pavé numérique iOS avec le séparateur
          // décimal, sans les touches de navigation d'un clavier complet.
          inputMode="decimal"
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="num min-h-touch w-full bg-transparent text-ink outline-none placeholder:text-ink-muted/50"
        />
        {suffix && <span className="num shrink-0 text-[13px] text-ink-muted">{suffix}</span>}
      </div>
    </label>
  )
}

/** Sélecteur segmenté. Chaque segment respecte la hauteur tactile. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabledOptions = [],
  dense = false,
}: {
  options: { value: T; label: string; hint?: string }[]
  value: T
  onChange: (value: T) => void
  disabledOptions?: T[]
  /** Barre serrée : pour les réglages fréquents posés au-dessus du graphique,
      où chaque pixel rendu est une bougie de plus. La cible reste large de la
      pleine largeur disponible, et haute de 38 px sur téléphone. */
  dense?: boolean
}) {
  return (
    <div
      className={`flex gap-1 rounded-md border border-border bg-elevated ${
        dense ? 'p-0.5' : 'p-1'
      }`}
    >
      {options.map((option) => {
        const active = option.value === value
        const disabled = disabledOptions.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            title={option.hint}
            className={`flex-1 whitespace-nowrap rounded px-2 text-[12px] font-semibold transition-colors ${
              dense ? 'min-h-[38px] md:min-h-[30px]' : 'min-h-touch'
            } ${
              active
                ? 'bg-accent text-white'
                : disabled
                  ? 'text-ink-muted/40'
                  : 'text-ink-muted hover:bg-border/60 active:bg-border'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** Ligne libellé / valeur, alignée sur la grille des chiffres. */
export function DataRow({
  label,
  value,
  hint,
  tone = 'normal',
}: {
  label: string
  value: ReactNode
  hint?: string
  tone?: 'normal' | 'up' | 'down' | 'muted'
}) {
  const tones = {
    normal: 'text-ink',
    up: 'text-up',
    down: 'text-down',
    muted: 'text-ink-muted',
  } as const

  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2 text-[13px]">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className="text-right">
        <span className={`num ${tones[tone]}`}>{value}</span>
        {hint && <span className="block text-[11px] text-ink-muted">{hint}</span>}
      </dd>
    </div>
  )
}

/** Encadré d'avertissement ou d'erreur. */
export function Notice({
  tone,
  title,
  items,
}: {
  tone: 'warn' | 'error'
  title: string
  items: string[]
}) {
  if (items.length === 0) return null
  const styles =
    tone === 'error'
      ? 'border-down/50 bg-down/10 text-down'
      : 'border-warn/40 bg-warn/10 text-warn'

  return (
    <div className={`rounded-md border p-3 text-[12px] leading-relaxed ${styles}`}>
      <p className="mb-1 font-semibold">{title}</p>
      <ul className="space-y-1 text-ink">
        {items.map((item) => (
          <li key={item} className="flex gap-1.5">
            <span aria-hidden>{tone === 'error' ? '✖' : '⚠'}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
